import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Trophy, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NDABanner } from "@/components/NDABanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [ndaAgreed, setNdaAgreed] = useState(false);
  const navigate = useNavigate();

  const handleValidateCode = async (e) => {
    e.preventDefault();
    
    if (!ndaAgreed) {
      toast.error("Please agree to the Confidentiality & NDA terms to proceed");
      return;
    }
    
    if (!code.trim()) {
      toast.error("Please enter your claim code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/validate-code`, {
        code: code.toUpperCase()
      });

      if (response.data.valid) {
        toast.success("Code verified! Proceeding to claim form...");
        navigate("/claim", { state: { code: code.toUpperCase(), amount: response.data.amount } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or already claimed code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 relative overflow-hidden">
      {/* Floating Balloons Background */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.img
          src="https://images.unsplash.com/photo-1698285439446-2ad560e31a17?w=400"
          alt=""
          className="absolute top-20 right-10 w-32 h-32 object-contain opacity-40 floating-balloon"
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 0.4, y: 0 }}
          transition={{ duration: 1 }}
        />
        <motion.img
          src="https://images.unsplash.com/photo-1698285439446-2ad560e31a17?w=400"
          alt=""
          className="absolute bottom-20 left-10 w-40 h-40 object-contain opacity-30 floating-balloon"
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 0.3, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        />
      </div>

      <div className="container mx-auto px-4 py-12 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-10 h-10 text-secondary" />
            <h1 className="font-heading text-4xl md:text-5xl text-primary tracking-tight uppercase">
              MEGA MILLIONS
            </h1>
            <Trophy className="w-10 h-10 text-secondary" />
          </div>
          <p className="text-lg md:text-xl text-foreground/80 font-medium">Official Prize Claims Portal</p>
        </motion.div>

        {/* NDA Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <NDABanner agreed={ndaAgreed} onAgreeChange={setNdaAgreed} />
        </motion.div>

        {/* Hero Section */}
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* Left: Input Section */}
          <motion.div
            className="order-2 md:order-1"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-white/80 backdrop-blur-xl p-8 md:p-12 rounded-3xl shadow-2xl border-4 border-purple-100">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-8 h-8 text-primary" />
                <h2 className="font-heading text-3xl md:text-4xl text-primary uppercase tracking-tight">
                  Claim Your Prize
                </h2>
              </div>
              
              <p className="text-foreground/70 mb-8 text-lg">
                Enter your unique claim code to verify your winning ticket and start the claim process.
              </p>

              <form onSubmit={handleValidateCode} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-primary mb-3 uppercase tracking-wide">
                    Claim Code
                  </label>
                  <Input
                    data-testid="claim-code-input"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="MEGA-XXXXX"
                    className="h-20 text-4xl text-center font-heading tracking-widest border-4 border-purple-200 focus:border-primary rounded-2xl shadow-inner uppercase"
                    maxLength={20}
                  />
                </div>

                <Button
                  data-testid="verify-code-button"
                  type="submit"
                  disabled={loading}
                  className="w-full h-16 text-xl font-bold rounded-full bg-gradient-to-r from-primary to-purple-700 hover:from-purple-700 hover:to-primary shadow-[0_6px_0_0_rgba(0,0,0,0.2)] active:shadow-none active:translate-y-[6px] transition-all"
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Right: Prize Showcase */}
          <motion.div
            className="order-1 md:order-2 space-y-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Grand Prize Card */}
            <div className="bg-gradient-to-br from-primary via-purple-600 to-accent p-8 rounded-3xl shadow-2xl text-white animate-pulse-glow">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="w-10 h-10" />
                <h3 className="font-heading text-2xl uppercase">Grand Prize</h3>
              </div>
              <p className="font-heading text-6xl md:text-7xl mb-2">$5.5M</p>
              <p className="text-white/90 text-lg">Life-changing money waiting for you!</p>
            </div>

            {/* Car Prize Image */}
            <motion.div
              className="rounded-3xl overflow-hidden shadow-2xl"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src="https://images.unsplash.com/photo-1712611407735-d9d6ff87c64b?w=800"
                alt="Luxury prize cars"
                className="w-full h-64 object-cover"
              />
              <div className="bg-white p-6">
                <p className="font-bold text-xl text-primary">Plus Amazing Prizes!</p>
                <p className="text-foreground/70">Cars, vacations, and more</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Admin Link */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <button
            onClick={() => navigate("/admin")}
            className="text-sm text-foreground/50 hover:text-primary transition-colors underline"
          >
            Admin Dashboard
          </button>
        </motion.div>
      </div>
    </div>
  );
}